<script setup>
import { reactive, computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const props = defineProps({
  preselectedContactIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'success']);
const store = useStore();
const { t } = useI18n();

const allInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const uiFlags = useMapGetter('campaignBatches/getUIFlags');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const state = reactive({
  name: '',
  category: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  createConversation: true,
});

const templateParserRef = ref(null);

const rules = {
  name: { required, minLength: minLength(1) },
  inboxId: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

// Only show WhatsApp Cloud inboxes
const whatsappCloudInboxes = computed(() =>
  (allInboxes.value || []).filter(
    inbox =>
      inbox.provider === 'whatsapp_cloud' ||
      inbox.additional_attributes?.provider === 'whatsapp_cloud'
  )
);

const inboxOptions = computed(() =>
  mapToOptions(whatsappCloudInboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.BATCH.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name', 'NAME'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
}));

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  // Build template_params from the template parser
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  const templateParams = currentTemplate
    ? {
        name: currentTemplate.name || '',
        namespace: currentTemplate.namespace || '',
        category: currentTemplate.category || 'UTILITY',
        language: currentTemplate.language || 'en_US',
        processed_params: parserData?.processedParams || {},
      }
    : {};

  const batchData = {
    name: state.name,
    category: state.category || null,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    create_conversation: state.createConversation,
    template_params: templateParams,
    source_type: props.preselectedContactIds.length ? 'manual' : 'manual',
    contact_ids: props.preselectedContactIds.length ? props.preselectedContactIds : undefined,
  };

  try {
    const result = await store.dispatch('campaignBatches/create', {
      campaign_batch: batchData,
    });
    useAlert(t('CAMPAIGN.BATCH.FORM.API.SUCCESS'));
    emit('success', result);
  } catch (error) {
    useAlert(t('CAMPAIGN.BATCH.FORM.API.ERROR'));
  }
};

const handleCancel = () => emit('close');
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <div
        @click.stop
      class="w-full max-w-lg bg-n-solid-2 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-lg max-h-[85vh] overflow-y-auto"
    >
      <div class="p-6 flex flex-col gap-6">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.BATCH.FORM.TITLE') }}
          </h3>
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            @click="handleCancel"
          />
        </div>

        <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
          <Input
            v-model="state.name"
            :label="t('CAMPAIGN.BATCH.FORM.NAME.LABEL')"
            :placeholder="t('CAMPAIGN.BATCH.FORM.NAME.PLACEHOLDER')"
            :message="formErrors.name"
            :message-type="formErrors.name ? 'error' : 'info'"
          />

          <Input
            v-model="state.category"
            :label="t('CAMPAIGN.BATCH.FORM.CATEGORY.LABEL')"
            :placeholder="t('CAMPAIGN.BATCH.FORM.CATEGORY.PLACEHOLDER')"
          />

          <div class="flex flex-col gap-1">
            <label
              for="inbox"
              class="mb-0.5 text-sm font-medium text-n-slate-12"
            >
              {{ t('CAMPAIGN.BATCH.FORM.INBOX.LABEL') }}
            </label>
            <ComboBox
              id="inbox"
              v-model="state.inboxId"
              :options="inboxOptions"
              :has-error="!!formErrors.inbox"
              :placeholder="t('CAMPAIGN.BATCH.FORM.INBOX.PLACEHOLDER')"
              :message="formErrors.inbox"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />
          </div>

          <div v-if="state.inboxId" class="flex flex-col gap-1">
            <label
              for="template"
              class="mb-0.5 text-sm font-medium text-n-slate-12"
            >
              {{ t('CAMPAIGN.BATCH.FORM.TEMPLATE.LABEL') }}
            </label>
            <ComboBox
              id="template"
              v-model="state.templateId"
              :options="templateOptions"
              :placeholder="t('CAMPAIGN.BATCH.FORM.TEMPLATE.PLACEHOLDER')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />
            <p class="mt-1 text-xs text-n-slate-11">
              {{ t('CAMPAIGN.BATCH.FORM.TEMPLATE.INFO') }}
            </p>
          </div>

          <WhatsAppTemplateParser
            v-if="selectedTemplate"
            ref="templateParserRef"
            :template="selectedTemplate"
          />

          <Input
            v-model="state.scheduledAt"
            :label="t('CAMPAIGN.BATCH.FORM.SCHEDULED_AT.LABEL')"
            type="datetime-local"
            :min="currentDateTime"
            :placeholder="t('CAMPAIGN.BATCH.FORM.SCHEDULED_AT.PLACEHOLDER')"
          />

          <div class="flex items-center gap-2">
            <input
              id="createConversation"
              v-model="state.createConversation"
              type="checkbox"
              class="w-4 h-4 text-n-blue-9 bg-n-alpha-black2 border-n-slate-6 rounded focus:ring-n-blue-9 focus:ring-2"
            />
            <label for="createConversation" class="text-sm font-medium text-n-slate-12">
              Criar conversa na caixa de entrada
            </label>
          </div>


          <div class="flex gap-3 justify-between items-center w-full pt-2">
            <Button
              variant="faded"
              color="slate"
              type="button"
              :label="t('CAMPAIGN.BATCH.FORM.BUTTONS.CANCEL')"
              class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
              @click="handleCancel"
            />
            <Button
              :label="t('CAMPAIGN.BATCH.FORM.BUTTONS.CREATE')"
              class="w-full"
              type="submit"
              :is-loading="isCreating"
              :disabled="isCreating"
            />
          </div>
        </form>
      </div>
    </div>
    </div>
  </Teleport>
</template>
